/**
 * Speech Analytics History Component
 * Shows improvement over time with charts for pace, filler words, confidence, etc.
 */

import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { Button } from '../ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import {
    TrendingUp, TrendingDown, Minus, Clock, MessageSquare,
    Shield, Volume2, Sparkles, Award, Target, Calendar,
    BarChart3, Loader2, RefreshCw, Zap, AlertCircle
} from 'lucide-react';

const SpeechAnalyticsHistory = ({ compact = false }) => {
    const { api } = useAuth();
    const [loading, setLoading] = useState(true);
    const [summary, setSummary] = useState(null);
    const [trends, setTrends] = useState(null);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState('overview');

    useEffect(() => {
        fetchAnalytics();
    }, []);

    const fetchAnalytics = async () => {
        setLoading(true);
        setError(null);
        try {
            const [summaryRes, trendsRes] = await Promise.all([
                api.get('/speech-analytics/summary'),
                api.get('/speech-analytics/trends?days=30')
            ]);

            if (summaryRes.data.success) {
                setSummary(summaryRes.data.summary);
            }
            if (trendsRes.data.success) {
                setTrends(trendsRes.data.trends);
            }
        } catch (err) {
            console.error('Failed to fetch speech analytics:', err);
            setError('Failed to load speech analytics');
        } finally {
            setLoading(false);
        }
    };

    const getTrendIcon = (improved) => {
        if (improved === true) return <TrendingUp className="w-4 h-4 text-emerald-400" />;
        if (improved === false) return <TrendingDown className="w-4 h-4 text-rose-400" />;
        return <Minus className="w-4 h-4 text-white/40" />;
    };

    const getTrendColor = (improved) => {
        if (improved === true) return 'text-emerald-400';
        if (improved === false) return 'text-rose-400';
        return 'text-white/40';
    };

    const formatChange = (change, inverse = false) => {
        if (change === 0) return '0';
        const isPositive = inverse ? change < 0 : change > 0;
        const prefix = isPositive ? '+' : '';
        return `${prefix}${change.toFixed(1)}`;
    };

    if (loading) {
        return (
            <Card className="bg-[hsl(240,6%,10%)] border-white/10">
                <CardContent className="p-8 flex items-center justify-center">
                    <Loader2 className="w-8 h-8 text-indigo-400 animate-spin" />
                </CardContent>
            </Card>
        );
    }

    if (error) {
        return (
            <Card className="bg-[hsl(240,6%,10%)] border-white/10">
                <CardContent className="p-8 text-center">
                    <AlertCircle className="w-12 h-12 text-rose-400 mx-auto mb-4" />
                    <p className="text-white/60">{error}</p>
                    <Button onClick={fetchAnalytics} variant="outline" className="mt-4">
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Retry
                    </Button>
                </CardContent>
            </Card>
        );
    }

    if (!summary || summary.total_responses === 0) {
        return (
            <Card className="bg-[hsl(240,6%,10%)] border-white/10">
                <CardContent className="p-8 text-center">
                    <Sparkles className="w-16 h-16 text-indigo-400 mx-auto mb-4 opacity-50" />
                    <h3 className="text-xl font-bold text-white mb-2">No Speech Data Yet</h3>
                    <p className="text-white/60 max-w-md mx-auto">
                        Start practicing voice responses in training modules to track your improvement over time.
                    </p>
                </CardContent>
            </Card>
        );
    }

    const improvement = summary.improvement;
    const hasImprovement = improvement?.has_enough_data;

    // Compact view for dashboard
    if (compact) {
        return (
            <Card className="bg-gradient-to-br from-indigo-500/10 to-purple-500/10 border-indigo-500/20">
                <CardHeader className="pb-2">
                    <CardTitle className="flex items-center gap-2 text-lg">
                        <Sparkles className="w-5 h-5 text-indigo-400" />
                        Speech Coaching Progress
                    </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="grid grid-cols-3 gap-4">
                        <div className="text-center">
                            <p className="text-2xl font-bold text-white">
                                {Math.round(summary.avg_overall_score || 0)}
                            </p>
                            <p className="text-xs text-white/50">Avg Score</p>
                        </div>
                        <div className="text-center">
                            <p className="text-2xl font-bold text-white">
                                {summary.total_responses}
                            </p>
                            <p className="text-xs text-white/50">Responses</p>
                        </div>
                        <div className="text-center">
                            <p className="text-2xl font-bold text-white">
                                {(summary.avg_filler_density || 0).toFixed(1)}%
                            </p>
                            <p className="text-xs text-white/50">Filler Rate</p>
                        </div>
                    </div>
                    
                    {hasImprovement && (
                        <div className="pt-2 border-t border-white/10">
                            <div className="flex items-center justify-between">
                                <span className="text-sm text-white/60">Overall Improvement</span>
                                <div className={`flex items-center gap-1 ${getTrendColor(improvement.overall_score?.improved)}`}>
                                    {getTrendIcon(improvement.overall_score?.improved)}
                                    <span className="font-semibold">
                                        {formatChange(improvement.overall_score?.change || 0)}
                                    </span>
                                </div>
                            </div>
                        </div>
                    )}
                </CardContent>
            </Card>
        );
    }

    // Full view
    return (
        <div className="space-y-6" data-testid="speech-analytics-history">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-2xl font-bold text-white flex items-center gap-3">
                        <Sparkles className="w-7 h-7 text-indigo-400" />
                        Speech Coaching History
                    </h2>
                    <p className="text-white/60 mt-1">
                        Track your speaking improvement over time
                    </p>
                </div>
                <Button 
                    onClick={fetchAnalytics} 
                    variant="outline" 
                    size="sm"
                    className="border-white/20 text-white hover:bg-white/10"
                >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Refresh
                </Button>
            </div>

            {/* Summary Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card className="bg-white/5 border-white/10">
                    <CardContent className="p-4">
                        <div className="flex items-center justify-between mb-2">
                            <MessageSquare className="w-5 h-5 text-blue-400" />
                            <Badge variant="secondary" className="bg-blue-500/20 text-blue-400 border-blue-500/30 text-xs">
                                Total
                            </Badge>
                        </div>
                        <p className="text-3xl font-bold text-white">{summary.total_responses}</p>
                        <p className="text-xs text-white/50 mt-1">Voice Responses</p>
                    </CardContent>
                </Card>

                <Card className="bg-white/5 border-white/10">
                    <CardContent className="p-4">
                        <div className="flex items-center justify-between mb-2">
                            <Target className="w-5 h-5 text-emerald-400" />
                            {hasImprovement && getTrendIcon(improvement.overall_score?.improved)}
                        </div>
                        <p className="text-3xl font-bold text-white">
                            {Math.round(summary.avg_overall_score || 0)}
                        </p>
                        <p className="text-xs text-white/50 mt-1">Average Score</p>
                    </CardContent>
                </Card>

                <Card className="bg-white/5 border-white/10">
                    <CardContent className="p-4">
                        <div className="flex items-center justify-between mb-2">
                            <Clock className="w-5 h-5 text-amber-400" />
                            <span className="text-xs text-white/50">
                                {Math.round(summary.avg_pace || 0)} WPM
                            </span>
                        </div>
                        <p className="text-3xl font-bold text-white">
                            {summary.total_words?.toLocaleString() || 0}
                        </p>
                        <p className="text-xs text-white/50 mt-1">Total Words Spoken</p>
                    </CardContent>
                </Card>

                <Card className="bg-white/5 border-white/10">
                    <CardContent className="p-4">
                        <div className="flex items-center justify-between mb-2">
                            <AlertCircle className="w-5 h-5 text-rose-400" />
                            {hasImprovement && getTrendIcon(improvement.filler_density?.improved)}
                        </div>
                        <p className="text-3xl font-bold text-white">
                            {summary.total_fillers || 0}
                        </p>
                        <p className="text-xs text-white/50 mt-1">Total Filler Words</p>
                    </CardContent>
                </Card>
            </div>

            {/* Improvement Cards */}
            {hasImprovement && (
                <Card className="bg-gradient-to-br from-emerald-500/10 to-teal-500/10 border-emerald-500/20">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2 text-emerald-400">
                            <Award className="w-5 h-5" />
                            Your Progress (First 10 vs Last 10 Responses)
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="grid md:grid-cols-3 gap-6">
                            {/* Overall Score */}
                            <div className="p-4 rounded-xl bg-black/20">
                                <div className="flex items-center justify-between mb-3">
                                    <span className="text-sm text-white/60">Overall Score</span>
                                    {getTrendIcon(improvement.overall_score?.improved)}
                                </div>
                                <div className="flex items-end gap-3">
                                    <div>
                                        <p className="text-xs text-white/40">First</p>
                                        <p className="text-xl font-semibold text-white/70">
                                            {improvement.overall_score?.first}
                                        </p>
                                    </div>
                                    <div className="text-2xl text-white/30">→</div>
                                    <div>
                                        <p className="text-xs text-white/40">Recent</p>
                                        <p className="text-xl font-bold text-white">
                                            {improvement.overall_score?.recent}
                                        </p>
                                    </div>
                                </div>
                                <div className={`mt-2 text-sm font-semibold ${getTrendColor(improvement.overall_score?.improved)}`}>
                                    {formatChange(improvement.overall_score?.change || 0)} points
                                </div>
                            </div>

                            {/* Filler Words */}
                            <div className="p-4 rounded-xl bg-black/20">
                                <div className="flex items-center justify-between mb-3">
                                    <span className="text-sm text-white/60">Filler Word Rate</span>
                                    {getTrendIcon(improvement.filler_density?.improved)}
                                </div>
                                <div className="flex items-end gap-3">
                                    <div>
                                        <p className="text-xs text-white/40">First</p>
                                        <p className="text-xl font-semibold text-white/70">
                                            {improvement.filler_density?.first}%
                                        </p>
                                    </div>
                                    <div className="text-2xl text-white/30">→</div>
                                    <div>
                                        <p className="text-xs text-white/40">Recent</p>
                                        <p className="text-xl font-bold text-white">
                                            {improvement.filler_density?.recent}%
                                        </p>
                                    </div>
                                </div>
                                <div className={`mt-2 text-sm font-semibold ${getTrendColor(improvement.filler_density?.improved)}`}>
                                    {formatChange(improvement.filler_density?.change || 0, true)}% {improvement.filler_density?.improved ? 'reduction' : 'increase'}
                                </div>
                            </div>

                            {/* Confidence */}
                            <div className="p-4 rounded-xl bg-black/20">
                                <div className="flex items-center justify-between mb-3">
                                    <span className="text-sm text-white/60">Confidence</span>
                                    {getTrendIcon(improvement.confidence?.improved)}
                                </div>
                                <div className="flex items-end gap-3">
                                    <div>
                                        <p className="text-xs text-white/40">First</p>
                                        <p className="text-xl font-semibold text-white/70">
                                            {improvement.confidence?.first}%
                                        </p>
                                    </div>
                                    <div className="text-2xl text-white/30">→</div>
                                    <div>
                                        <p className="text-xs text-white/40">Recent</p>
                                        <p className="text-xl font-bold text-white">
                                            {improvement.confidence?.recent}%
                                        </p>
                                    </div>
                                </div>
                                <div className={`mt-2 text-sm font-semibold ${getTrendColor(improvement.confidence?.improved)}`}>
                                    {formatChange(improvement.confidence?.change || 0)} points
                                </div>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Trend Charts */}
            {trends && trends.data_points > 0 && (
                <Card className="bg-white/5 border-white/10">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <BarChart3 className="w-5 h-5 text-indigo-400" />
                            30-Day Trends
                        </CardTitle>
                        <CardDescription>
                            Daily averages over the past 30 days
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <Tabs value={activeTab} onValueChange={setActiveTab}>
                            <TabsList className="grid grid-cols-4 mb-6">
                                <TabsTrigger value="overview">Score</TabsTrigger>
                                <TabsTrigger value="pace">Pace</TabsTrigger>
                                <TabsTrigger value="fillers">Fillers</TabsTrigger>
                                <TabsTrigger value="confidence">Confidence</TabsTrigger>
                            </TabsList>

                            <TabsContent value="overview">
                                <TrendChart 
                                    data={trends.overall_scores}
                                    dates={trends.dates}
                                    label="Overall Score"
                                    color="indigo"
                                    suffix=""
                                />
                            </TabsContent>

                            <TabsContent value="pace">
                                <TrendChart 
                                    data={trends.pace}
                                    dates={trends.dates}
                                    label="Words Per Minute"
                                    color="blue"
                                    suffix=" WPM"
                                    optimalRange={[120, 160]}
                                />
                            </TabsContent>

                            <TabsContent value="fillers">
                                <TrendChart 
                                    data={trends.filler_density}
                                    dates={trends.dates}
                                    label="Filler Word Density"
                                    color="rose"
                                    suffix="%"
                                    inverse={true}
                                />
                            </TabsContent>

                            <TabsContent value="confidence">
                                <TrendChart 
                                    data={trends.confidence}
                                    dates={trends.dates}
                                    label="Confidence Score"
                                    color="emerald"
                                    suffix="%"
                                />
                            </TabsContent>
                        </Tabs>
                    </CardContent>
                </Card>
            )}

            {/* Top Fillers */}
            {summary.top_fillers && Object.keys(summary.top_fillers).length > 0 && (
                <Card className="bg-rose-500/5 border-rose-500/20">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2 text-rose-400">
                            <AlertCircle className="w-5 h-5" />
                            Your Most Common Filler Words
                        </CardTitle>
                        <CardDescription>
                            Focus on eliminating these from your speech
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div className="flex flex-wrap gap-3">
                            {Object.entries(summary.top_fillers).map(([word, count]) => (
                                <div 
                                    key={word}
                                    className="px-4 py-2 rounded-xl bg-rose-500/10 border border-rose-500/20"
                                >
                                    <span className="text-rose-300 font-medium">&quot;{word}&quot;</span>
                                    <span className="text-rose-400/60 ml-2">× {count}</span>
                                </div>
                            ))}
                        </div>
                        <p className="mt-4 text-sm text-white/50">
                            💡 Tip: Practice pausing silently instead of using these words
                        </p>
                    </CardContent>
                </Card>
            )}
        </div>
    );
};

/**
 * Simple Trend Chart Component (CSS-based, no external library)
 */
function TrendChart(props) {
    const data = props.data;
    const dates = props.dates;
    const label = props.label;
    const color = props.color;
    const suffix = props.suffix || '';
    const optimalRange = props.optimalRange;
    const inverse = props.inverse || false;

    if (!data || data.length === 0) {
        return (
            <div className="text-center py-8 text-white/50">
                No data available for this period
            </div>
        );
    }

    const max = Math.max(...data);
    const min = Math.min(...data);
    const range = max - min || 1;
    const latest = data[data.length - 1];
    const first = data[0];
    const change = latest - first;
    const improved = inverse ? change < 0 : change > 0;

    const getColorClasses = (colorName) => {
        const classes = {
            indigo: { bar: 'bg-indigo-500', text: 'text-indigo-400', bg: 'bg-indigo-500/20' },
            blue: { bar: 'bg-blue-500', text: 'text-blue-400', bg: 'bg-blue-500/20' },
            rose: { bar: 'bg-rose-500', text: 'text-rose-400', bg: 'bg-rose-500/20' },
            emerald: { bar: 'bg-emerald-500', text: 'text-emerald-400', bg: 'bg-emerald-500/20' }
        };
        return classes[colorName] || classes.indigo;
    };
    const colors = getColorClasses(color);

    const renderTrendIcon = () => {
        if (improved) {
            return <TrendingUp className={'w-4 h-4 ' + colors.text} />;
        }
        if (change === 0) {
            return <Minus className="w-4 h-4 text-white/40" />;
        }
        const iconClass = inverse ? colors.text : 'text-rose-400';
        return <TrendingDown className={'w-4 h-4 ' + iconClass} />;
    };

    const formatChangeValue = () => {
        const prefix = change > 0 ? '+' : '';
        return prefix + change.toFixed(1) + suffix;
    };

    const renderBar = (value, index) => {
        const height = ((value - min) / range) * 100;
        const isLatest = index === data.length - 1;
        const barClass = isLatest ? colors.bar : (colors.bar + ' opacity-50');
        const barStyle = { height: Math.max(height, 5) + '%' };
        
        return (
            <div 
                key={'bar-' + index}
                className="flex-1 flex flex-col items-center gap-1 group relative"
            >
                <div 
                    className={'w-full rounded-t transition-all ' + barClass}
                    style={barStyle}
                />
                <div className="absolute bottom-full mb-2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                    <div className="bg-black/90 text-white text-xs px-2 py-1 rounded whitespace-nowrap">
                        {dates[index]}: {value}{suffix}
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div>
            <div className="flex items-center justify-between mb-6">
                <div>
                    <p className="text-white/50 text-sm">{label}</p>
                    <p className={'text-3xl font-bold ' + colors.text}>
                        {latest}{suffix}
                    </p>
                </div>
                <div className={'flex items-center gap-2 px-3 py-1 rounded-full ' + colors.bg}>
                    {renderTrendIcon()}
                    <span className={colors.text}>
                        {formatChangeValue()}
                    </span>
                </div>
            </div>

            {optimalRange && (
                <div className="mb-4 text-xs text-white/40">
                    Optimal range: {optimalRange[0]} - {optimalRange[1]}{suffix}
                </div>
            )}

            <div className="flex items-end gap-1 h-32">
                {data.map(renderBar)}
            </div>

            <div className="flex justify-between mt-2 text-xs text-white/30">
                <span>{dates[0]}</span>
                <span>{dates[dates.length - 1]}</span>
            </div>
        </div>
    );
}

export default SpeechAnalyticsHistory;
